import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
    ArrowBack, Cancel, CheckCircle, EmojiEvents, Portrait, Refresh, SentimentDissatisfied
} from "@mui/icons-material";
import {
    WhosThatProReveal,
    WhosThatProRunView,
    endWhosThatProRun,
    nextWhosThatProRound,
    startWhosThatProRun,
    submitWhosThatProGuess,
    whosThatProPhotoUrl
} from "../services/MinigameService";
import GameLeaderboardPanel from "../components/games/GameLeaderboardPanel";
import styles from "./WhosThatProPage.module.css";

type Difficulty = "easy" | "hard";
type Phase = "loading" | "playing" | "won" | "lost";

// One horizontal face band. Its answer stays on the server until it is solved or the round ends.
interface Band {
    options: string[];
    locked: boolean;
    // Set when solved, or when the answer is revealed at the end of a lost round.
    revealedName: string | null;
    wrongPicks: string[];
}

const BANDS = 4;
const MAX_STRIKES = 5;
const ROUND_SECS = 90;
const BEST_KEY = "whosThatProBest";

const WhosThatProPage: React.FC = () => {
    const { t } = useTranslation("pages");
    const [difficulty, setDifficulty] = useState<Difficulty>("easy");
    const [bands, setBands] = useState<Band[]>([]);
    const [phase, setPhase] = useState<Phase>("loading");
    const [strikes, setStrikes] = useState(MAX_STRIKES);
    const [score, setScore] = useState(0);
    const [roundNum, setRoundNum] = useState(1);
    const [elapsed, setElapsed] = useState(0);
    const [flash, setFlash] = useState(-1);
    const [busy, setBusy] = useState(false);
    const [streak, setStreak] = useState(0);
    const [, setBest] = useState<number>(() => {
        const v = Number(localStorage.getItem(BEST_KEY));
        return Number.isFinite(v) ? v : 0;
    });
    // The server round being shown: the photo URLs are keyed on it.
    const [round, setRound] = useState<{ runId: string; roundNo: number; bandAspects: number[] } | null>(null);
    const [roundSecs, setRoundSecs] = useState(ROUND_SECS);
    // Bumped whenever the run's score can have changed, so the leaderboard panel refetches.
    const [boardKey, setBoardKey] = useState(0);
    const startedAt = useRef<number>(Date.now());
    const roundSecsRef = useRef(ROUND_SECS);
    // The run in play. A won round continues it; a lost round (or a new difficulty) starts another.
    const runRef = useRef<{ id: string; over: boolean } | null>(null);
    // Each load gets a number; a response for an older one (a quick difficulty/mode toggle) is ignored.
    const loadSeq = useRef(0);
    const [loadFailed, setLoadFailed] = useState(false);
    const phaseRef = useRef<Phase>("loading");
    phaseRef.current = phase;

    const features = [
        t('whosThatPro.feature1'),
        t('whosThatPro.feature2'),
        t('whosThatPro.feature3'),
        t('whosThatPro.feature4'),
    ];

    const applyRound = (view: WhosThatProRunView) => {
        runRef.current = { id: view.runId, over: false };
        setRound({ runId: view.runId, roundNo: view.round.roundNo, bandAspects: view.round.bandAspects });
        setBands(view.round.options.map((options) => ({ options, locked: false, revealedName: null, wrongPicks: [] })));
        setStrikes(view.round.strikes);
        setStreak(view.score);
        roundSecsRef.current = view.round.secondsLeft;
        setRoundSecs(view.round.secondsLeft);
        startedAt.current = Date.now();
        setElapsed(0);
        setPhase("playing");
    };

    const loadRound = useCallback(async (firstLoad: boolean) => {
        const seq = ++loadSeq.current;
        setPhase("loading");
        setLoadFailed(false);
        setFlash(-1);
        setBusy(false);
        const run = runRef.current;
        const view = run && !run.over && !firstLoad
            ? await nextWhosThatProRound(run.id)
            : await startWhosThatProRun(difficulty);
        if (seq !== loadSeq.current) return;
        if (!view) {
            setLoadFailed(true);
            return;
        }
        if (!firstLoad) setRoundNum((n) => n + 1);
        applyRound(view);
    }, [difficulty]);

    useEffect(() => {
        setStreak(0);
        setScore(0);
        setRoundNum(1);
        loadRound(true);
    }, [loadRound]);

    // Round timer — losing when the clock runs out. The server holds the same deadline.
    useEffect(() => {
        const timer = setInterval(() => {
            if (phaseRef.current !== "playing") return;
            const el = Math.floor((Date.now() - startedAt.current) / 1000);
            setElapsed(Math.min(el, roundSecsRef.current));
            if (el >= roundSecsRef.current) endLoss();
        }, 500);
        return () => clearInterval(timer);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const endWin = (runScore: number) => {
        setPhase("won");
        setStreak(runScore);
        setBest((b) => {
            if (runScore <= b) return b;
            localStorage.setItem(BEST_KEY, String(runScore));
            return runScore;
        });
        setBoardKey((k) => k + 1);
    };

    // Unsolved bands get their answer filled in from the server's end-of-round reveal.
    const applyReveal = (reveal: WhosThatProReveal[]) => {
        setBands((current) => current.map((b, i) => b.locked ? b : { ...b, revealedName: reveal[i]?.name ?? null }));
    };

    const markRunOver = (reveal: WhosThatProReveal[] | null) => {
        if (runRef.current) runRef.current.over = true;
        setPhase("lost");
        setStreak(0);
        if (reveal) applyReveal(reveal);
        setBoardKey((k) => k + 1);
    };

    // The clock ran out or the player gave up: end the run on the server, which reveals the round.
    const endLoss = async () => {
        if (phaseRef.current !== "playing") return;
        const run = runRef.current;
        markRunOver(null);
        if (!run) return;
        const reveal = await endWhosThatProRun(run.id);
        if (reveal) applyReveal(reveal);
    };

    const handlePick = async (bandIdx: number, name: string) => {
        const run = runRef.current;
        if (phase !== "playing" || busy || !run) return;
        const band = bands[bandIdx];
        if (!band || band.locked || band.wrongPicks.includes(name)) return;
        setBusy(true);
        const res = await submitWhosThatProGuess(run.id, bandIdx, name);
        setBusy(false);
        // The clock may have run out while the guess was in flight.
        if (!res || phaseRef.current !== "playing") return;
        if (res.correct) {
            setBands((current) => current.map((b, i) => i === bandIdx ? { ...b, locked: true, revealedName: name } : b));
            setScore((s) => s + 100);
            if (res.roundWon) endWin(res.score);
            return;
        }
        // A wrong pick costs a strike. The answer stays hidden until the round is over.
        setStrikes(res.strikes);
        if (!res.timedOut) {
            setFlash(bandIdx);
            setTimeout(() => setFlash(-1), 350);
            setBands((current) => current.map((b, i) => i === bandIdx ? { ...b, wrongPicks: [...b.wrongPicks, name] } : b));
        }
        if (res.runOver) markRunOver(res.reveal);
    };

    const over = phase === "won" || phase === "lost";
    const solved = bands.filter((b) => b.locked).length;
    // All four bands share one layout; fall back to equal quarters if it's ever absent.
    const bandAspects = round?.bandAspects?.length === BANDS
        ? round.bandAspects
        : Array.from({ length: BANDS }, () => BANDS);
    const remaining = Math.max(0, roundSecs - elapsed);
    const timeText = `${Math.floor(remaining / 60)}:${String(remaining % 60).padStart(2, '0')}`;

    const rowClass = (i: number, b: Band) => {
        if (b.locked) return `${styles.row} ${styles.rowSolved}`;
        if (flash === i) return `${styles.row} ${styles.rowFlash}`;
        if (phase === "lost") return `${styles.row} ${styles.rowLost}`;
        return styles.row;
    };

    return (
        <div className={styles.page}>
            <Link to="/games" className={styles.backLink}>
                <ArrowBack sx={{ fontSize: 18 }} />
                {t('whosThatPro.back')}
            </Link>

            {/* ===== Header ===== */}
            <div className={styles.header}>
                <div className={styles.identity}>
                    <span className={styles.gameIcon}>
                        <Portrait sx={{ fontSize: 28 }} />
                    </span>
                    <div>
                        <h1 className={styles.title}>Who's That Pro?</h1>
                        <p className={styles.tagline}>{t('whosThatPro.tagline')}</p>
                    </div>
                </div>
                <div className={styles.headerRight}>
                    <div className={styles.toggleGroup}>
                        <button
                            className={`${styles.toggleBtn} ${difficulty === "easy" ? styles.toggleBtnActive : ""}`}
                            onClick={() => setDifficulty("easy")}
                        >
                            {t('whosThatPro.easy')}
                        </button>
                        <button
                            className={`${styles.toggleBtn} ${difficulty === "hard" ? styles.toggleBtnActive : ""}`}
                            onClick={() => setDifficulty("hard")}
                        >
                            {t('whosThatPro.hard')}
                        </button>
                    </div>
                    <div className={styles.roundTile}>
                        <div className={styles.roundLabel}>{t('whosThatPro.round')}</div>
                        <div className={styles.roundValue}>{String(roundNum).padStart(2, '0')}</div>
                    </div>
                </div>
            </div>

            {/* ===== Stat bar ===== */}
            <div className={styles.statBar}>
                <div className={styles.statCell} style={{ minWidth: 110 }}>
                    <div className={styles.statLabel}>{t('whosThatPro.timeLeft')}</div>
                    <div className={`${styles.statValue} ${remaining <= 15 ? styles.statTimeLow : ''}`}>{timeText}</div>
                </div>
                <div className={styles.statCell}>
                    <div className={styles.statLabel}>{t('whosThatPro.solved')}</div>
                    <div className={`${styles.statValue} ${styles.statSolved}`}>{solved}/{BANDS}</div>
                </div>
                <div className={`${styles.statCell} ${styles.statCellWide}`}>
                    <div className={styles.statLabel}>{t('whosThatPro.strikes')}</div>
                    <div className={styles.strikes}>
                        {Array.from({ length: MAX_STRIKES }).map((_, i) => (
                            <span key={i} className={`${styles.strikeMark} ${i < strikes ? styles.strikeOn : ''}`} />
                        ))}
                    </div>
                </div>
                <div className={styles.statCell}>
                    <div className={styles.statLabel}>{t('whosThatPro.score')}</div>
                    <div className={styles.statValue}>{score}</div>
                </div>
                <div className={styles.statCell}>
                    <div className={styles.statLabel}>{t('whosThatPro.streak')}</div>
                    <div className={`${styles.statValue} ${styles.statStreak}`}>{streak}</div>
                </div>
            </div>

            {phase === "loading" && (
                <div className={styles.loading}>
                    {loadFailed
                        ? <>{t('gamesLeaderboard.loadFailed')} <button className={styles.nextBtn} onClick={() => loadRound(false)}>{t('gamesLeaderboard.retry')}</button></>
                        : t('whosThatPro.loading')}
                </div>
            )}

            {phase !== "loading" && bands.length === BANDS && (
                <div className={styles.game}>
                    {/* ===== Composite ===== */}
                    <div className={styles.compositePanel}>
                        <div className={styles.panelLabel}>
                            <span className={styles.panelDot} />
                            {t('whosThatPro.composite')}
                        </div>
                        <div className={styles.composite}>
                            {bands.map((b, i) => (
                                <div
                                    key={`${round?.roundNo}-${i}`}
                                    className={`${styles.band} ${i < BANDS - 1 ? styles.bandDivider : ''}`}
                                    // The backend cuts the bands on facial landmarks, so they are
                                    // deliberately uneven. Reserving each one's aspect ratio up front
                                    // keeps the composite from reflowing as the images arrive.
                                    style={{ aspectRatio: bandAspects[i] }}
                                >
                                    <img
                                        src={round ? whosThatProPhotoUrl(round.runId, round.roundNo, i) : undefined}
                                        alt=""
                                        className={styles.bandImg}
                                    />
                                    <div className={`${styles.bandOverlay} ${b.locked ? styles.bandSolved : ''}`}>
                                        <span className={`${styles.bandNum} ${b.locked ? styles.bandNumSolved : ''}`}>
                                            {String(i + 1).padStart(2, '0')}
                                        </span>
                                        {(b.locked || (over && b.revealedName)) && (
                                            <span className={styles.bandAnswer}>{b.revealedName}</span>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className={styles.panelFoot}>
                            <span className={styles.panelHint}>{t('whosThatPro.bandHint')}</span>
                            {difficulty === "hard" && <span className={styles.hardBadge}>{t('whosThatPro.hardMode')}</span>}
                        </div>
                    </div>

                    {/* ===== Guess rows ===== */}
                    <div className={styles.guessPanel}>
                        <div className={styles.guessTop}>
                            <span className={styles.guessLabel}>{t('whosThatPro.identifyAll')}</span>
                            {phase === "playing" && (
                                <button className={styles.giveUp} onClick={endLoss}>{t('whosThatPro.giveUp')}</button>
                            )}
                        </div>
                        <h2 className={styles.guessTitle}>{t('whosThatPro.pickRight')}</h2>

                        <div className={styles.rows}>
                            {bands.map((b, i) => {
                                const showDone = b.locked || (over && b.revealedName);
                                return (
                                    <div className={rowClass(i, b)} key={`${round?.roundNo}-${i}`}>
                                        <div className={styles.rowHead}>
                                            <span className={`${styles.rowNum} ${b.locked ? styles.rowNumSolved : ''}`}>
                                                {String(i + 1).padStart(2, '0')}
                                            </span>
                                            <span className={styles.rowFeature}>{features[i]}</span>
                                            {showDone && (
                                                <span className={`${styles.rowDone} ${b.locked ? styles.rowDoneOk : styles.rowDoneMiss}`}>
                                                    {b.locked
                                                        ? <CheckCircle sx={{ fontSize: 17 }} />
                                                        : <Cancel sx={{ fontSize: 17 }} />}
                                                    {b.locked ? b.revealedName : t('whosThatPro.missed', { name: b.revealedName })}
                                                </span>
                                            )}
                                        </div>
                                        {!showDone && (
                                            <div className={styles.options}>
                                                {b.options.map((name) => (
                                                    <button
                                                        key={name}
                                                        className={`${styles.option} ${b.wrongPicks.includes(name) ? styles.optionWrong : ''}`}
                                                        onClick={() => handlePick(i, name)}
                                                        disabled={over || busy || b.wrongPicks.includes(name)}
                                                    >
                                                        {name}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                        </div>

                        <div className={styles.guessFoot}>{t('whosThatPro.footHint')}</div>

                        {over && (
                            <div className={`${styles.resultBlock} ${phase === "won" ? styles.resultWon : styles.resultLost}`}>
                                <div className={`${styles.resultMsg} ${phase === "won" ? styles.resultMsgWon : styles.resultMsgLost}`}>
                                    {phase === "won"
                                        ? <EmojiEvents sx={{ fontSize: 22 }} />
                                        : <SentimentDissatisfied sx={{ fontSize: 22 }} />}
                                    {phase === "won"
                                        ? t('whosThatPro.won', { points: BANDS * 100 })
                                        : t('whosThatPro.lost')}
                                </div>
                                <div>
                                    <button className={styles.nextBtn} onClick={() => loadRound(false)}>
                                        <Refresh sx={{ fontSize: 18 }} />
                                        {t('whosThatPro.newRound')}
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}

            <GameLeaderboardPanel game="whos-that-pro" variant={difficulty} refreshKey={boardKey} />
        </div>
    );
};

export default WhosThatProPage;
